import React, { useState } from 'react';
import { useTranslation } from '@/store/languageStore';
import type { TimeEntry, ProjectTask, ProjectSource } from '@/types/projects';
import { projectApi } from '@/services/projects/projectApi';
import { PlusIcon, TrashIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

import DateInput from '@/components/common/DateInput';

interface Props {
  source: ProjectSource;
  projectId: number;
  entries: TimeEntry[];
  tasks: ProjectTask[];
  onRefresh: () => void;
  showBudget?: boolean;
  budget?: number;
  actualCost?: number;
}

export const TimeEntryPanel: React.FC<Props> = ({
  source, projectId, entries, tasks, onRefresh, showBudget, budget, actualCost,
}) => {
  const { t } = useTranslation();
  const [hours, setHours] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [taskId, setTaskId] = useState('');
  const [notes, setNotes] = useState('');
  const [billable, setBillable] = useState(true);
  const [adding, setAdding] = useState(false);

  const totalHours = entries.reduce((s, e) => s + Number(e.hours), 0);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hours) return;
    setAdding(true);
    try {
      await projectApi.createTimeEntry(source, projectId, {
        hours: parseFloat(hours),
        entry_date: date,
        task_id: taskId ? Number(taskId) : undefined,
        notes: notes || undefined,
        billable: source === 'crm' ? billable : undefined,
      });
      setHours('');
      setNotes('');
      onRefresh();
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id: number) => {
    await projectApi.deleteTimeEntry(source, projectId, id);
    onRefresh();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-textDefault">{t('projects.timeTracking')}</h3>
        {source === 'crm' && (
          <button
            type="button"
            onClick={() => projectApi.exportTimeCsv(projectId)}
            className="flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            CSV
          </button>
        )}
      </div>
      {showBudget && (
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="p-2 bg-bgElevated rounded border border-border">
            <span className="text-textSecondary">{t('projects.budget')}</span>
            <p className="font-semibold">{budget?.toLocaleString('sv-SE') ?? '—'} kr</p>
          </div>
          <div className="p-2 bg-bgElevated rounded border border-border">
            <span className="text-textSecondary">{t('projects.actualCost')}</span>
            <p className="font-semibold">{actualCost?.toLocaleString('sv-SE') ?? '—'} kr</p>
          </div>
          <div className="p-2 bg-bgElevated rounded border border-border">
            <span className="text-textSecondary">{t('projects.totalHours')}</span>
            <p className="font-semibold">{totalHours.toFixed(1)} h</p>
          </div>
        </div>
      )}
      <form onSubmit={handleAdd} className="flex flex-wrap gap-2 items-end">
        <input
          type="number"
          step="0.25"
          min="0.25"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          placeholder={t('projects.hours')}
          className="w-20 px-2 py-2 border border-border rounded-lg bg-bgBase text-sm"
        />
        <DateInput value={date}
          onChange={(e) => setDate(e.target.value)}
          className="px-2 py-2 border border-border rounded-lg bg-bgBase text-sm"
        />
        <select
          value={taskId}
          onChange={(e) => setTaskId(e.target.value)}
          className="px-2 py-2 border border-border rounded-lg bg-bgBase text-sm"
        >
          <option value="">{t('projects.noTask')}</option>
          {tasks.map((task) => (
            <option key={task.id} value={task.id}>{task.title}</option>
          ))}
        </select>
        {source === 'crm' && (
          <label className="flex items-center gap-1 text-sm">
            <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} />
            {t('projects.billable')}
          </label>
        )}
        <button
          type="submit"
          disabled={adding}
          className="flex items-center gap-1 px-3 py-2 bg-primary text-white rounded-lg text-sm"
        >
          <PlusIcon className="w-4 h-4" />
          {t('projects.logTime')}
        </button>
      </form>
      <ul className="divide-y divide-border max-h-48 overflow-y-auto">
        {entries.map((e) => (
          <li key={e.id} className="flex justify-between py-2 text-sm">
            <span>{e.entry_date} · {e.hours}h · {e.user_name || e.user_id}</span>
            <button type="button" onClick={() => handleDelete(e.id)} className="text-red-500">
              <TrashIcon className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
